import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AppState, Member, TaskInput } from "./types";
import { api, ApiError, createShareUrl, generateToken, getStoredToken, storeToken } from "./lib/api";
import { calendarRange, monthKey, todayInJapan } from "./lib/date";
import { CalendarView } from "./components/CalendarView";
import { DateDetail } from "./components/DateDetail";
import { CalendarIcon, CheckIcon, SettingsIcon, SparkleIcon, UserIcon } from "./components/Icons";
import { MemberPicker } from "./components/MemberPicker";
import { SettingsView } from "./components/SettingsView";
import { SetupScreen } from "./components/SetupScreen";

type Phase = "loading" | "bootstrap" | "missing-link" | "ready";
type View = "today" | "calendar" | "settings";
const MEMBER_KEY = "osouji-member-id";

export default function App() {
  const today = todayInJapan();
  const [phase, setPhase] = useState<Phase>("loading");
  const [token, setToken] = useState<string | null>(null);
  const [state, setState] = useState<AppState | null>(null);
  const [view, setView] = useState<View>("today");
  const [selectedDate, setSelectedDate] = useState(today);
  const [displayMonth, setDisplayMonth] = useState(monthKey(today));
  const [selectedMemberId, setSelectedMemberId] = useState(() => localStorage.getItem(MEMBER_KEY) ?? "");
  const [initialBusy, setInitialBusy] = useState(false);
  const [globalBusy, setGlobalBusy] = useState(false);
  const [busyTaskIds, setBusyTaskIds] = useState<Set<string>>(new Set());
  const [noteBusy, setNoteBusy] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const stateRequestSequence = useRef(0);

  const range = useMemo(() => calendarRange(displayMonth), [displayMonth]);

  const showMessage = useCallback((message: string, type: "success" | "error" = "success") => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  const loadState = useCallback(async (accessToken: string, background = false) => {
    const requestSequence = ++stateRequestSequence.current;
    try {
      const next = await api.state(accessToken, range.from, range.to);
      if (requestSequence !== stateRequestSequence.current) return;
      setState(next);
      setError("");
      setPhase("ready");
    } catch (caught) {
      if (requestSequence !== stateRequestSequence.current) return;
      if (caught instanceof ApiError && caught.status === 401) {
        localStorage.removeItem("osouji-access-token");
        setPhase("missing-link");
        setState(null);
        setToken(null);
        setError("共有URLを確認してください。");
      } else if (!background) {
        setError(caught instanceof Error ? caught.message : "データを読み込めませんでした。");
      }
    }
  }, [range.from, range.to]);

  useEffect(() => {
    let active = true;
    const initialize = async () => {
      try {
        const accessToken = getStoredToken();
        const status = await api.status();
        if (!active) return;
        if (!status.initialized) {
          setPhase("bootstrap");
          return;
        }
        if (!accessToken) {
          setPhase("missing-link");
          return;
        }
        setToken(accessToken);
        await loadState(accessToken);
      } catch (caught) {
        if (active) {
          setError(caught instanceof Error ? caught.message : "アプリを開始できませんでした。");
          setPhase("loading");
        }
      }
    };
    void initialize();
    return () => { active = false; };
  }, [loadState]);

  useEffect(() => {
    if (phase !== "ready" || !token || view === "settings") return;
    const refresh = () => void loadState(token, true);
    const timer = window.setInterval(refresh, 15_000);
    const onVisibility = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [phase, token, view, loadState]);

  const bootstrap = async (names: string[]) => {
    setInitialBusy(true);
    setError("");
    const accessToken = generateToken();
    try {
      await api.bootstrap(accessToken, names.map((name) => name.trim()));
      storeToken(accessToken);
      setToken(accessToken);
      await loadState(accessToken);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "初期設定に失敗しました。");
    } finally {
      setInitialBusy(false);
    }
  };

  const useShareLink = (value: string) => {
    try {
      const parsed = new URL(value.trim(), window.location.origin);
      const accessToken = new URLSearchParams(parsed.hash.slice(1)).get("key");
      if (!accessToken) throw new Error("共有URLに共有キーがありません。");
      storeToken(accessToken);
      window.location.reload();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "共有URLが正しくありません。");
    }
  };

  const selectMember = (id: string) => {
    localStorage.setItem(MEMBER_KEY, id);
    setSelectedMemberId(id);
  };

  const refresh = async () => {
    if (token) await loadState(token);
  };

  const toggleRecord = async (taskId: string, completed: boolean) => {
    if (!token || !selectedMemberId) return;
    setBusyTaskIds((current) => new Set(current).add(taskId));
    try {
      const result = await api.updateRecord(token, selectedDate, taskId, completed, selectedMemberId);
      setState((current) => {
        if (!current) return current;
        const records = current.records.filter((record) => !(record.task_id === taskId && record.record_date === selectedDate));
        if (result.record) records.push(result.record);
        return { ...current, records };
      });
    } catch (caught) {
      showMessage(caught instanceof Error ? caught.message : "記録を保存できませんでした。", "error");
    } finally {
      setBusyTaskIds((current) => {
        const next = new Set(current);
        next.delete(taskId);
        return next;
      });
    }
  };

  const saveNote = async (content: string) => {
    if (!token || !selectedMemberId) return;
    setNoteBusy(true);
    try {
      const result = await api.updateNote(token, selectedDate, content, selectedMemberId);
      setState((current) => {
        if (!current) return current;
        const notes = current.notes.filter((note) => note.record_date !== selectedDate);
        if (result.note) notes.push(result.note);
        return { ...current, notes };
      });
      showMessage("備考を保存しました。");
    } catch (caught) {
      showMessage(caught instanceof Error ? caught.message : "備考を保存できませんでした。", "error");
    } finally {
      setNoteBusy(false);
    }
  };

  const runSettingMutation = async (operation: () => Promise<unknown>, success: string) => {
    setGlobalBusy(true);
    try {
      await operation();
      await refresh();
      showMessage(success);
    } catch (caught) {
      showMessage(caught instanceof Error ? caught.message : "変更を保存できませんでした。", "error");
    } finally {
      setGlobalBusy(false);
    }
  };

  const chooseDate = (date: string) => {
    setSelectedDate(date);
    const nextMonth = monthKey(date);
    if (nextMonth !== displayMonth) setDisplayMonth(nextMonth);
  };

  const changeMonth = (nextMonth: string) => {
    setDisplayMonth(nextMonth);
    setSelectedDate(`${nextMonth}-01`);
  };

  const goToToday = () => {
    const nextToday = todayInJapan();
    setSelectedDate(nextToday);
    setDisplayMonth(monthKey(nextToday));
    setView("today");
  };

  if (phase === "loading") {
    return <LoadingScreen error={error} onRetry={() => window.location.reload()} />;
  }

  if (phase === "bootstrap" || phase === "missing-link") {
    return (
      <SetupScreen
        mode={phase}
        busy={initialBusy}
        error={error}
        onBootstrap={bootstrap}
        onUseShareLink={useShareLink}
      />
    );
  }

  if (!state || !token) return null;

  const selectedMember = state.members.find((member) => member.id === selectedMemberId);
  if (!selectedMember) return <MemberPicker members={state.members} onSelect={selectMember} />;

  return (
    <div className="app-shell">
      <header className="app-header">
        <button className="brand-button" onClick={goToToday} aria-label="今日の掃除へ">
          <span className="small-brand-mark"><SparkleIcon /></span>
          <span><small>OUR CLEAN HOME</small><strong>おそうじ記録</strong></span>
        </button>
        <button className="current-user" onClick={() => setView("settings")}>
          <UserIcon /><span>{selectedMember.name}</span>
        </button>
      </header>

      <main className="main-content">
        {view === "today" && (
          <DateDetail
            date={selectedDate}
            state={state}
            busyTaskIds={busyTaskIds}
            noteBusy={noteBusy}
            onToggle={toggleRecord}
            onSaveNote={saveNote}
          />
        )}
        {view === "calendar" && (
          <>
            <CalendarView
              month={displayMonth}
              selectedDate={selectedDate}
              state={state}
              onMonthChange={changeMonth}
              onDateSelect={chooseDate}
            />
            <DateDetail
              date={selectedDate}
              state={state}
              busyTaskIds={busyTaskIds}
              noteBusy={noteBusy}
              onToggle={toggleRecord}
              onSaveNote={saveNote}
            />
          </>
        )}
        {view === "settings" && (
          <SettingsView
            state={state}
            token={token}
            selectedMemberId={selectedMemberId}
            busy={globalBusy}
            onSelectMember={selectMember}
            onUpdateMembers={(members: Pick<Member, "id" | "name">[]) => runSettingMutation(() => api.updateMembers(token, members), "名前を更新しました。")}
            onCreateTask={(input: TaskInput) => runSettingMutation(() => api.createTask(token, input), "掃除項目を追加しました。")}
            onUpdateTask={(id: string, input: TaskInput) => runSettingMutation(() => api.updateTask(token, id, input), "掃除項目を更新しました。")}
            onDeleteTask={(id: string) => runSettingMutation(() => api.deleteTask(token, id), "掃除項目を削除しました。")}
            onReorderTasks={(ids: string[]) => runSettingMutation(() => api.reorderTasks(token, ids), "並び順を更新しました。")}
            onCopyShareUrl={async () => {
              try {
                await navigator.clipboard.writeText(createShareUrl(token));
                showMessage("共有URLをコピーしました。");
              } catch {
                showMessage("共有URLをコピーできませんでした。", "error");
              }
            }}
            onRotateToken={async () => {
              setGlobalBusy(true);
              try {
                const result = await api.rotateToken(token);
                storeToken(result.token);
                setToken(result.token);
                await loadState(result.token);
                await navigator.clipboard.writeText(createShareUrl(result.token)).catch(() => undefined);
                showMessage("新しい共有URLを発行し、コピーしました。");
              } catch (caught) {
                showMessage(caught instanceof Error ? caught.message : "共有URLを再発行できませんでした。", "error");
              } finally {
                setGlobalBusy(false);
              }
            }}
            onMessage={showMessage}
          />
        )}
      </main>

      <nav className="bottom-nav" aria-label="メインメニュー">
        <button className={view === "today" ? "is-active" : ""} onClick={goToToday}><CheckIcon /><span>今日</span></button>
        <button className={view === "calendar" ? "is-active" : ""} onClick={() => setView("calendar")}><CalendarIcon /><span>カレンダー</span></button>
        <button className={view === "settings" ? "is-active" : ""} onClick={() => setView("settings")}><SettingsIcon /><span>設定</span></button>
      </nav>

      {toast && <div className={`toast toast-${toast.type}`} role="status">{toast.message}</div>}
    </div>
  );
}

function LoadingScreen({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <main className="loading-screen">
      <div className="brand-mark"><SparkleIcon /></div>
      {error ? (
        <><p className="form-error">{error}</p><button className="secondary-button" onClick={onRetry}>もう一度試す</button></>
      ) : (
        <><div className="loading-dots"><span /><span /><span /></div><p>おうちの記録を準備しています</p></>
      )}
    </main>
  );
}
